/**
 * Batch Genealogy — Batch Detail side panel.
 *
 * Opened by clicking a node in the lineage graph (or "View batch detail" in the
 * All deviations panel). Sections, top → bottom:
 *   header (stage + status chip + batch no. + close)
 *   quantities grid (Planned, Actual, Yield, Mfg date, Retest, Storage,
 *                    Stage X Intermediate / Final Product description)
 *   Deviations           — only when the batch has deviations
 *   Analytical results   — in-process & release pass/fail
 *   Key intermediates consumed — only when intermediates exist
 *   All materials consumed (count) — full line-item list
 *   Equipment            — chips
 */
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CallMadeIcon from '@material-ui/icons/CallMade';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { STATUS, stageRole } from '../statusConfig';
import { fetchBatchDetail } from '../api';

const useStyles = makeStyles(() => ({
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 1400 },
  panel: {
    position: 'fixed', top: 0, right: 0, height: '100vh', width: 460, maxWidth: '92vw', background: '#fff',
    boxShadow: '-8px 0 24px rgba(15,23,42,0.18)', zIndex: 1401, display: 'flex', flexDirection: 'column',
  },
  head: { padding: '16px 20px', borderBottom: '1px solid #eef2f7' },
  chipRow: { display: 'flex', alignItems: 'center', gap: 8 },
  stepChip: { fontSize: 12, fontWeight: 700, color: '#000000', background: '#eef2f7', borderRadius: 6, padding: '2px 8px' },
  statusChip: { fontSize: 12, fontWeight: 700, borderRadius: 12, padding: '2px 10px' },
  close: { marginLeft: 'auto', cursor: 'pointer', color: '#000000', border: '1px solid #e5e7eb', borderRadius: 8, padding: 4, background: '#fff', display: 'inline-flex', fontFamily: 'inherit' },
  batchId: { fontSize: 22, fontWeight: 800, color: '#000000', marginTop: 8 },
  body: { padding: '4px 20px 28px', overflowY: 'auto', flex: 1 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 14, columnGap: 16, padding: '16px 0', borderBottom: '1px solid #f1f5f9' },
  fullRow: { gridColumn: '1 / -1' },
  k: { fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#000000', textTransform: 'capitalize' },
  v: { fontSize: 14, fontWeight: 400, color: '#333333', marginTop: 2 },
  section: { marginTop: 20 },
  sectionTitle: { fontSize: 12, fontWeight: 800, letterSpacing: 0.6, color: '#000000', textTransform: 'uppercase', marginBottom: 10 },
  devCard: { border: '1px solid #fde68a', background: '#fffdf5', borderRadius: 10, padding: 14 },
  devHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  devNum: { fontSize: 13, fontWeight: 800, color: '#0f172a' },
  devStatus: { fontSize: 12, fontWeight: 800, letterSpacing: 0.4, color: '#b45309', background: '#fef3c7', borderRadius: 10, padding: '2px 8px', textTransform: 'uppercase' },
  devType: { marginLeft: 'auto', fontSize: 12, color: '#333333' },
  devDesc: { fontSize: 13, color: '#333333', lineHeight: 1.5, margin: '10px 0' },
  openDevBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, color: '#000000', cursor: 'pointer', fontFamily: 'inherit' },
  analytics: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  analyticBox: { border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' },
  analyticLbl: { fontSize: 12, fontWeight: 700, color: '#000000', textTransform: 'uppercase', marginBottom: 6 },
  passN: { color: '#16a34a', fontWeight: 400 },
  failN: { color: '#b91c1c', fontWeight: 400 },
  intRow: { display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e7eb', borderRadius: 10, padding: '11px 14px', marginBottom: 8 },
  intDot: { width: 8, height: 8, borderRadius: '50%', background: '#6d28d9' },
  intBatch: { fontSize: 13, fontWeight: 400, color: '#333333' },
  intQty: { marginLeft: 'auto', fontSize: 13, fontWeight: 400, color: '#333333' },
  matRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
  matDesc: { fontSize: 13, fontWeight: 400, color: '#333333', lineHeight: 1.4 },
  matSub: { fontSize: 12, color: '#333333', marginTop: 3 },
  matQty: { marginLeft: 'auto', fontSize: 13, fontWeight: 400, color: '#333333', whiteSpace: 'nowrap' },
  keyBadge: { fontSize: 12, fontWeight: 800, color: '#6d28d9', background: '#ede9fe', borderRadius: 6, padding: '1px 6px', marginLeft: 6 },
  countPill: { fontSize: 12, fontWeight: 700, color: '#000000', background: '#eef2f7', borderRadius: 10, padding: '1px 8px', marginLeft: 6 },
  equipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  equipChip: { fontSize: 12, fontWeight: 400, color: '#333333', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 12px' },
  loading: { padding: 40, textAlign: 'center', color: '#333333', fontSize: 14 },
}));

const fmtQty = (q, uom) => (q == null || q === '' ? '—' : `${q}${uom ? ' ' + uom : ''}`);

// `step` is the report's step entry for this batch ({ id, label, desc }); its
// product description is shown as the "Stage X Intermediate / Final Product"
// field (it used to sit in the stage rail beside the lineage graph).
const BatchDetailDrawer = ({ batchId, step, onClose, onOpenDeviation }) => {
  const classes = useStyles();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchBatchDetail(batchId).then((res) => {
      if (alive) { setDetail(res.data); setLoading(false); }
    });
    return () => { alive = false; };
  }, [batchId]);

  const s = detail ? (STATUS[detail.status] || STATUS.released) : STATUS.released;
  const stageCode = (step && step.id) || (detail && detail.stage) || '';
  const stageFieldLabel = `Stage ${stageCode} ${stageRole(step)}`;

  return (
    <>
      <div className={classes.overlay} onClick={onClose} />
      <div className={classes.panel} role="dialog" aria-label={`Batch ${batchId} detail`}>
        <div className={classes.head}>
          <div className={classes.chipRow}>
            <span className={classes.stepChip}>Step {detail ? detail.stage : ''}</span>
            <span className={classes.statusChip} style={{ background: s.bg, color: s.text }}>{s.label}</span>
            <button type="button" className={classes.close} onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></button>
          </div>
          <div className={classes.batchId}>{batchId}</div>
        </div>

        <div className={classes.body}>
          {loading ? (
            <div className={classes.loading}>Loading batch detail…</div>
          ) : !detail ? (
            <div className={classes.loading}>Batch detail unavailable.</div>
          ) : (
            <>
              {/* quantities grid */}
              <div className={classes.grid}>
                {[
                  ['Planned', fmtQty(detail.planned, detail.uom)],
                  ['Actual', fmtQty(detail.actual, detail.uom)],
                  ['Yield', detail.yieldPct != null ? `${detail.yieldPct}%` : '—'],
                  ['Mfg date', detail.mfgDate || '—'],
                  ['Retest', detail.retestDate || '—'],
                  ['Storage', detail.storage || '—'],
                ].map(([k, v]) => (
                  <div key={k}><div className={classes.k}>{k}</div><div className={classes.v}>{v}</div></div>
                ))}
                <div className={classes.fullRow}>
                  <div className={classes.k}>{stageFieldLabel}</div>
                  <div className={classes.v}>{(step && step.desc) || '—'}</div>
                </div>
              </div>

              {/* deviations */}
              {detail.deviations && detail.deviations.length > 0 && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>Deviations</div>
                  {detail.deviations.map((d) => (
                    <div className={classes.devCard} key={d.number}>
                      <div className={classes.devHead}>
                        <span className={classes.devNum}>{d.number}</span>
                        <span className={classes.devStatus}>{d.status}</span>
                        <span className={classes.devType}>{d.type}</span>
                      </div>
                      <div className={classes.devDesc}>{d.desc}</div>
                      <button type="button" className={classes.openDevBtn} onClick={() => onOpenDeviation && onOpenDeviation(d)}>
                        Open Deviation Tab <CallMadeIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* analytical results */}
              {detail.analytical && (
              <div className={classes.section}>
                <div className={classes.sectionTitle}>Analytical Results</div>
                <div className={classes.analytics}>
                  <div className={classes.analyticBox}>
                    <div className={classes.analyticLbl}>In-process</div>
                    <div><span className={classes.passN}>{detail.analytical.inproc.pass}</span> pass&nbsp;&nbsp;<span className={classes.failN}>{detail.analytical.inproc.fail}</span> fail</div>
                  </div>
                  <div className={classes.analyticBox}>
                    <div className={classes.analyticLbl}>Release</div>
                    <div><span className={classes.passN}>{detail.analytical.release.pass}</span> pass&nbsp;&nbsp;<span className={classes.failN}>{detail.analytical.release.fail}</span> fail</div>
                  </div>
                </div>
              </div>
              )}

              {/* key intermediates consumed */}
              {detail.keyIntermediates && detail.keyIntermediates.length > 0 && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>Key Intermediates Consumed</div>
                  {detail.keyIntermediates.map((it) => (
                    <div className={classes.intRow} key={it.batch}>
                      <span className={classes.intDot} />
                      <span className={classes.intBatch}>{it.batch}</span>
                      <span className={classes.intQty}>{fmtQty(it.qty, it.uom)}</span>
                      <ChevronRightIcon style={{ fontSize: 18, color: '#000000' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* all materials consumed */}
              {detail.materials && detail.materials.length > 0 && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>
                    All Materials Consumed <span className={classes.countPill}>{detail.materials.length}</span>
                  </div>
                  {detail.materials.map((m, i) => (
                    <div className={classes.matRow} key={`${m.code}-${m.lot}-${i}`}>
                      <div>
                        <div className={classes.matDesc}>
                          {m.desc}
                          {m.key && <span className={classes.keyBadge}>KEY</span>}
                        </div>
                        <div className={classes.matSub}>{m.code}{m.lot ? ` · Lot ${m.lot}` : ''}</div>
                      </div>
                      <div className={classes.matQty}>{fmtQty(m.qty, m.uom)}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* equipment */}
              {detail.equipment && detail.equipment.length > 0 && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>Equipment</div>
                  <div className={classes.equipRow}>
                    {detail.equipment.map((e, i) => (
                      <span className={classes.equipChip} key={i}>{e.type}{e.spec ? ` · ${e.spec}` : ''}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BatchDetailDrawer;
